import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, ChevronDown, RefreshCw } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import LoadingSpinner from '../common/LoadingSpinner';
import GymLogoUploadField from './GymLogoUploadField';

const inputClass =
    'w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white';

const compactInputClass =
    'w-full px-2.5 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white';

const fieldHintClass = 'mt-0.5 text-xs text-gray-500 dark:text-gray-400 leading-snug';

/** Stessa regola del backend: "Fit Gym" → fitgym */
export function slugifyGymUsernameBase(value) {
    const s = value
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-z0-9]+/g, '')
        .slice(0, 24);
    return s || 'palestra';
}

function generateTempPassword() {
    return `TEMP_${Math.random().toString(36).slice(2, 10)}`;
}

const defaultFormState = {
    name: '',
    email: '',
    phone: '',
    address: '',
    owner_name: '',
    owner_email: '',
    owner_password: '',
    max_users: 100,
    max_trainers: 10,
    plan: 'monthly',
    plan_active: true,
    billing_access: 'paid',
};

export default function GymCreateForm({ onSubmit, onCancel, initialValues = null, variant = 'default' }) {
    const isModal = variant === 'modal';
    const { t } = useTranslation();
    const { getAuthenticatedApi, isAdmin } = useAuth();
    const [formData, setFormData] = useState(() => ({
        ...defaultFormState,
        ...(initialValues || {}),
    }));
    const [suggestedUsername, setSuggestedUsername] = useState('');
    const [usernameLoading, setUsernameLoading] = useState(false);
    const [showAdvanced, setShowAdvanced] = useState(isModal);
    const [loading, setLoading] = useState(false);
    const [logoFile, setLogoFile] = useState(null);

    const fieldInput = isModal ? compactInputClass : inputClass;

    useEffect(() => {
        setFormData((prev) => (
            prev.owner_password ? prev : { ...prev, owner_password: generateTempPassword() }
        ));
    }, []);

    const fetchSuggestedUsername = useCallback(async (gymName) => {
        const trimmed = gymName.trim();
        if (!trimmed || !isAdmin()) {
            setSuggestedUsername(trimmed ? slugifyGymUsernameBase(trimmed) : '');
            return;
        }
        setUsernameLoading(true);
        try {
            const api = getAuthenticatedApi();
            const res = await api.get('/gyms.php', {
                params: { action: 'suggest_owner_username', gym_name: trimmed }
            });
            setSuggestedUsername(res.data?.username || slugifyGymUsernameBase(trimmed));
        } catch {
            setSuggestedUsername(slugifyGymUsernameBase(trimmed));
        } finally {
            setUsernameLoading(false);
        }
    }, [getAuthenticatedApi, isAdmin]);

    useEffect(() => {
        const timer = setTimeout(() => {
            fetchSuggestedUsername(formData.name);
        }, 350);
        return () => clearTimeout(timer);
    }, [formData.name, fetchSuggestedUsername]);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleGeneratePassword = () => {
        setFormData((prev) => ({ ...prev, owner_password: generateTempPassword() }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            await onSubmit(
                {
                    name: formData.name,
                    email: formData.email,
                    phone: formData.phone || undefined,
                    address: formData.address || undefined,
                    max_users: Number(formData.max_users),
                    max_trainers: Number(formData.max_trainers),
                    plan: formData.plan,
                    plan_active: formData.plan_active ? 1 : 0,
                    create_owner: true,
                    owner_password: formData.owner_password,
                    owner_name: formData.owner_name,
                    owner_email: formData.owner_email || formData.email,
                    ...(isModal ? { start_trial: true } : {}),
                    ...(!isModal && formData.billing_access === 'complimentary'
                        ? { billing_complimentary: true }
                        : {}),
                },
                isModal ? null : logoFile
            );
        } finally {
            setLoading(false);
        }
    };

    const wrapperClass = isModal ? 'w-full' : 'bg-white dark:bg-gray-800 rounded-lg shadow-md p-4 sm:p-6 w-full';

    return (
        <div className={wrapperClass}>
            <form onSubmit={handleSubmit} className={isModal ? 'space-y-3' : 'space-y-5'}>
                {isModal && (
                    <p className="text-xs text-amber-800 dark:text-amber-200 bg-amber-50 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-800 rounded-md px-3 py-2">
                        {t('admin.gymRequests.trialBanner')}
                    </p>
                )}

                <div className={`grid grid-cols-1 ${isModal ? 'lg:grid-cols-2 gap-3' : 'xl:grid-cols-2 gap-5 xl:gap-8'}`}>
                    <section className="min-w-0">
                        <h2 className={`font-semibold text-gray-900 dark:text-white ${isModal ? 'text-sm mb-1' : 'text-base'}`}>
                            {t('admin.gymManager.createSectionGym')}
                        </h2>
                        {!isModal && (
                            <p className={`${fieldHintClass} mb-3`}>
                                {t('admin.gymManager.createSectionGymHint')}
                            </p>
                        )}
                        <div className={`grid grid-cols-1 ${isModal ? 'gap-2' : 'sm:grid-cols-2 xl:grid-cols-12 gap-3 sm:gap-4'}`}>
                            <div className={isModal ? '' : 'sm:col-span-2 xl:col-span-12'}>
                                <label className={`block font-medium text-gray-700 dark:text-gray-300 mb-0.5 ${isModal ? 'text-xs' : 'text-sm mb-1'}`}>
                                    {t('admin.gymManager.fieldGymName')} *
                                </label>
                                <input
                                    type="text"
                                    name="name"
                                    value={formData.name}
                                    onChange={handleChange}
                                    required
                                    autoFocus
                                    className={fieldInput}
                                />
                            </div>
                            <div className={isModal ? 'grid grid-cols-2 gap-2' : 'contents'}>
                                <div className={isModal ? '' : 'sm:col-span-1 xl:col-span-5'}>
                                    <label className={`block font-medium text-gray-700 dark:text-gray-300 ${isModal ? 'text-xs mb-0.5' : 'text-sm mb-1'}`}>
                                        {t('admin.gymManager.fieldGymEmail')} *
                                    </label>
                                    <input
                                        type="email"
                                        name="email"
                                        value={formData.email}
                                        onChange={handleChange}
                                        required
                                        className={fieldInput}
                                    />
                                    {!isModal && (
                                        <p className={fieldHintClass}>{t('admin.gymManager.fieldGymEmailHint')}</p>
                                    )}
                                </div>
                                <div className={isModal ? '' : 'sm:col-span-1 xl:col-span-3'}>
                                    <label className={`block font-medium text-gray-700 dark:text-gray-300 ${isModal ? 'text-xs mb-0.5' : 'text-sm mb-1'}`}>
                                        {t('admin.gymManager.fieldPhone')}
                                    </label>
                                    <input
                                        type="tel"
                                        name="phone"
                                        value={formData.phone}
                                        onChange={handleChange}
                                        className={fieldInput}
                                    />
                                </div>
                            </div>
                            <div className={isModal ? '' : 'sm:col-span-2 xl:col-span-4'}>
                                <label className={`block font-medium text-gray-700 dark:text-gray-300 ${isModal ? 'text-xs mb-0.5' : 'text-sm mb-1'}`}>
                                    {t('admin.gymManager.fieldAddress')}
                                </label>
                                <input
                                    type="text"
                                    name="address"
                                    value={formData.address}
                                    onChange={handleChange}
                                    className={fieldInput}
                                />
                            </div>
                        </div>
                    </section>

                    <section
                        className={
                            isModal
                                ? 'min-w-0'
                                : 'min-w-0 xl:border-l xl:border-gray-200 xl:dark:border-gray-700 xl:pl-8 pt-5 xl:pt-0 border-t xl:border-t-0 border-gray-200 dark:border-gray-700'
                        }
                    >
                        <h2 className={`font-semibold text-gray-900 dark:text-white ${isModal ? 'text-sm mb-1' : 'text-base'}`}>
                            {t('admin.gymManager.createSectionAccess')}
                        </h2>
                        {!isModal && (
                            <p className={`${fieldHintClass} mb-3`}>
                                {t('admin.gymManager.createSectionAccessHint')}
                            </p>
                        )}
                        <div className={`grid grid-cols-1 ${isModal ? 'gap-2' : 'sm:grid-cols-2 xl:grid-cols-12 gap-3 sm:gap-4'}`}>
                            <div className={isModal ? '' : 'sm:col-span-2 xl:col-span-12'}>
                                <label className={`block font-medium text-gray-700 dark:text-gray-300 ${isModal ? 'text-xs mb-0.5' : 'text-sm mb-1'}`}>
                                    {t('admin.gymManager.fieldManagerName')} *
                                </label>
                                <input
                                    type="text"
                                    name="owner_name"
                                    value={formData.owner_name}
                                    onChange={handleChange}
                                    required
                                    className={fieldInput}
                                />
                            </div>
                            {!isModal && (
                                <>
                                    <div className="sm:col-span-1 xl:col-span-5">
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                            {t('admin.gymManager.fieldUsername')}
                                        </label>
                                        <div className="px-3 py-2 rounded-md border border-gray-200 bg-gray-50 dark:bg-gray-700/50 dark:border-gray-600 font-mono text-sm text-gray-800 dark:text-gray-100 min-h-[42px] flex items-center">
                                            {usernameLoading ? (
                                                <span className="text-gray-500">{t('common.loading')}</span>
                                            ) : suggestedUsername ? (
                                                suggestedUsername
                                            ) : (
                                                <span className="text-gray-400">-</span>
                                            )}
                                        </div>
                                        <p className={fieldHintClass}>{t('admin.gymManager.fieldUsernameHint')}</p>
                                    </div>
                                    <div className="sm:col-span-1 xl:col-span-7">
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                            {t('admin.gymManager.fieldPassword')} *
                                        </label>
                                        <div className="flex gap-2">
                                            <input
                                                type="text"
                                                name="owner_password"
                                                value={formData.owner_password}
                                                onChange={handleChange}
                                                required
                                                autoComplete="new-password"
                                                className={`${inputClass} font-mono`}
                                            />
                                            <button
                                                type="button"
                                                onClick={handleGeneratePassword}
                                                className="shrink-0 px-3 py-2 rounded-md border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                                                title={t('admin.gymManager.generatePassword')}
                                            >
                                                <RefreshCw size={18} />
                                            </button>
                                        </div>
                                        <p className={fieldHintClass}>{t('admin.gymManager.fieldPasswordHint')}</p>
                                    </div>
                                </>
                            )}
                            {isModal && (
                                <p className="text-xs text-gray-500 dark:text-gray-400 col-span-full">
                                    {t('admin.gymRequests.emailCredentialsHint', {
                                        email: formData.owner_email || formData.email,
                                        username: suggestedUsername || '…',
                                    })}
                                </p>
                            )}
                        </div>

                        {!isModal && (
                            <div className="mt-3 rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 dark:border-blue-800 dark:bg-blue-900/20 flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
                                <span className="font-medium text-sm text-blue-900 dark:text-blue-200 shrink-0">
                                    {t('admin.gymManager.autoCredentialsTitle')}
                                </span>
                                <p className="text-sm text-blue-800 dark:text-blue-300/90 sm:border-l sm:border-blue-300/50 sm:dark:border-blue-600/50 sm:pl-4">
                                    {t('admin.gymManager.autoCredentialsHint')}
                                </p>
                            </div>
                        )}
                    </section>
                </div>

                {!isModal && (
                    <section className="border-t border-gray-200 dark:border-gray-700 pt-5">
                        <GymLogoUploadField onFileChange={setLogoFile} compact />
                    </section>
                )}

                <section className={isModal ? '' : 'border-t border-gray-200 dark:border-gray-700 pt-3'}>
                    {!isModal && (
                        <button
                            type="button"
                            onClick={() => setShowAdvanced((v) => !v)}
                            className="flex items-center gap-2 text-sm font-medium text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white transition-colors"
                        >
                            <ChevronDown
                                size={18}
                                className={`transition-transform ${showAdvanced ? 'rotate-180' : ''}`}
                            />
                            {t('admin.gymManager.advancedSettings')}
                        </button>
                    )}
                    {(showAdvanced || isModal) && (
                        <div className={isModal ? 'grid grid-cols-2 gap-2' : 'mt-3 space-y-3'}>
                            {!isModal && (
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                        {t('admin.gymManager.fieldBillingAccess')}
                                    </label>
                                    <select
                                        name="billing_access"
                                        value={formData.billing_access}
                                        onChange={handleChange}
                                        className={inputClass}
                                    >
                                        <option value="paid">{t('admin.gymManager.billingAccessPaid')}</option>
                                        <option value="complimentary">{t('admin.gymManager.billingAccessComplimentary')}</option>
                                    </select>
                                    <p className={fieldHintClass}>{t('admin.gymManager.fieldBillingAccessHint')}</p>
                                </div>
                            )}
                            <div className={isModal ? 'contents' : 'grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-4'}>
                            <div>
                                <label className={`block font-medium text-gray-700 dark:text-gray-300 ${isModal ? 'text-xs mb-0.5' : 'text-sm mb-1'}`}>
                                    {t('admin.gymManager.fieldMaxUsers')}
                                </label>
                                <input
                                    type="number"
                                    name="max_users"
                                    value={formData.max_users}
                                    onChange={handleChange}
                                    min="1"
                                    max={isModal ? 5 : undefined}
                                    readOnly={isModal}
                                    className={`${fieldInput} ${isModal ? 'bg-gray-100 dark:bg-gray-600/50' : ''}`}
                                />
                            </div>
                            <div>
                                <label className={`block font-medium text-gray-700 dark:text-gray-300 ${isModal ? 'text-xs mb-0.5' : 'text-sm mb-1'}`}>
                                    {t('admin.gymManager.fieldMaxTrainers')}
                                </label>
                                <input
                                    type="number"
                                    name="max_trainers"
                                    value={formData.max_trainers}
                                    onChange={handleChange}
                                    min="1"
                                    className={fieldInput}
                                />
                            </div>
                            {!isModal && (
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    {t('admin.gymManager.fieldPlanFeatures')}
                                </label>
                                <select
                                    name="plan"
                                    value={formData.plan}
                                    onChange={handleChange}
                                    className={inputClass}
                                >
                                    <option value="start">Start</option>
                                    <option value="premium">Premium</option>
                                    <option value="elite">Elite</option>
                                </select>
                                <p className={fieldHintClass}>{t('admin.gymManager.fieldPlanFeaturesHint')}</p>
                            </div>
                            )}
                            {!isModal && (
                                <div className="flex items-end">
                                    <label className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300 cursor-pointer whitespace-nowrap">
                                        <input
                                            type="checkbox"
                                            name="plan_active"
                                            checked={formData.plan_active}
                                            onChange={handleChange}
                                            className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                        />
                                        {t('admin.gymManager.fieldPlanActive')}
                                    </label>
                                </div>
                            )}
                            </div>
                        </div>
                    )}
                </section>

                <div className={`flex flex-wrap items-center justify-end gap-2 sm:gap-3 ${isModal ? 'pt-1' : 'pt-3 border-t border-gray-200 dark:border-gray-700'}`}>
                    <button
                        type="button"
                        onClick={onCancel}
                        className={`bg-gray-500 hover:bg-gray-600 text-white rounded-md transition-colors ${isModal ? 'px-4 py-1.5 text-sm' : 'px-6 py-2.5'}`}
                    >
                        {t('common.cancel')}
                    </button>
                    <button
                        type="submit"
                        disabled={loading || !formData.name.trim()}
                        className={`bg-blue-600 hover:bg-blue-700 text-white rounded-md flex items-center gap-2 disabled:opacity-50 transition-colors font-medium ${isModal ? 'px-4 py-1.5 text-sm' : 'px-6 py-2.5'}`}
                    >
                        {loading ? <LoadingSpinner size="small" /> : <Plus size={isModal ? 16 : 20} />}
                        {t('admin.gymManager.submitCreate')}
                    </button>
                </div>
            </form>
        </div>
    );
}
